import { Check, ChevronLeft, ChevronRight, Minus, X } from '@tamagui/lucide-icons-2'
import { createPortal } from 'react-dom'
import Button from 'components/button'
import { AnimatePresence, SizableText, Spinner, View, XStack, YStack } from 'tamagui'
import { Z_INDEX } from './constants'
import { useOnboarding } from './OnboardingContext'
import LogoGuide from './steps/LogoGuide'
import XeroGuide from './steps/XeroGuide'
import CommissionGuide from './steps/CommissionGuide'
import WishlistGuide from './steps/WishlistGuide'
import Form6Guide from './steps/Form6Guide'
import InvoiceGuide from './steps/InvoiceGuide'

const STEP_COMPONENTS = [LogoGuide, XeroGuide, CommissionGuide, WishlistGuide, Form6Guide, InvoiceGuide]

const ProgressDots = () => {
    const { steps, currentStep, isStepComplete, setCurrentStep } = useOnboarding()

    return (
        <XStack
            alignItems="center"
            paddingHorizontal="$4"
            paddingTop="$3"
            paddingBottom="$5"
        >
            {steps.map((step, i) => {
                const completed = isStepComplete(i)
                const isCurrent = i === currentStep

                return (
                    <XStack key={i} alignItems="center" flex={1}>
                        {/* Step circle */}
                        <View
                            width={24}
                            height={24}
                            borderRadius={999}
                            alignItems="center"
                            justifyContent="center"
                            cursor="pointer"
                            onPress={() => setCurrentStep(i)}
                            hoverStyle={{ opacity: 0.8 }}
                            backgroundColor={completed ? '$statusOk' : isCurrent ? '$background' : '$backgroundStrong'}
                            borderWidth={completed ? 0 : isCurrent ? 2 : 1}
                            borderColor={isCurrent ? '$primary' : '$borderColorStrong'}
                        >
                            {completed ? (
                                <Check size={12} color="white" />
                            ) : (
                                <SizableText
                                    fontSize={11}
                                    fontWeight="600"
                                    color={isCurrent ? '$primary' : '$colorMuted'}
                                >
                                    {i + 1}
                                </SizableText>
                            )}
                        </View>

                        {/* Connector line */}
                        {i < steps.length - 1 && (
                            <View
                                flex={1}
                                height={2}
                                backgroundColor={completed ? '$statusOk' : '$borderColor'}
                                marginHorizontal="$1"
                            />
                        )}
                    </XStack>
                )
            })}
        </XStack>
    )
}

const CollapsedButton = () => {
    const { setExpanded, completedCount, steps } = useOnboarding()

    return createPortal(
        <View
            position="fixed"
            right={24}
            bottom={84}
            zIndex={Z_INDEX.GUIDE}
        >
            <Button
                size="$4"
                theme="primary"
                borderRadius="$6"
                elevation="$4"
                onPress={() => setExpanded(true)}
                paddingHorizontal="$4"
            >
                <XStack gap="$2" alignItems="center">
                    <SizableText color="white" fontWeight="600" size="$3">
                        Setup
                    </SizableText>
                    <View
                        backgroundColor="rgba(255,255,255,0.25)"
                        borderRadius="$6"
                        paddingHorizontal="$2"
                        paddingVertical="$1"
                    >
                        <SizableText color="white" size="$2" fontWeight="700">
                            {completedCount}/{steps.length}
                        </SizableText>
                    </View>
                </XStack>
            </Button>
        </View>,
        document.body,
    )
}

// Step flow indicator bar (collapsed) + expanded panel
function TourStepFlowUI() {
    const {
        tourSteps,
        tourCurrentStepIndex,
        tourHighlights,
        skipTourStep,
        exitTourStepFlow,
        expanded: guideExpanded,
        setExpanded: setGuideExpanded,
    } = useOnboarding()

    const currentStep = tourSteps[tourCurrentStepIndex]
    if (!currentStep) return null

    // Render via portal so it sits above Dialog.Portal (signing modals)
    return createPortal(
        <>
            {/* Collapsed: step indicator bar — sits above the AI help button */}
            {!guideExpanded && (
                <XStack
                    position="fixed"
                    bottom={84}
                    right={24}
                    zIndex={Z_INDEX.TOUR_FLOW}
                    backgroundColor="$background"
                    borderWidth={1}
                    borderColor="$primary"
                    borderRadius="$4"
                    elevation="$4"
                    alignItems="center"
                    gap="$2"
                    paddingHorizontal="$3"
                    paddingVertical="$2.5"
                    width={340}
                    maxWidth="calc(100vw - 48px)"
                    animation="quick"
                    cursor="pointer"
                    onPress={() => setGuideExpanded(true)}
                    hoverStyle={{ elevation: '$6' }}
                >
                    <View
                        width={26}
                        height={26}
                        borderRadius={13}
                        backgroundColor="$primary"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                    >
                        <SizableText size="$1" color="white" fontWeight="700">
                            {tourCurrentStepIndex + 1}/{tourSteps.length}
                        </SizableText>
                    </View>
                    <SizableText size="$3" flex={1} numberOfLines={1}>
                        {currentStep.description}
                    </SizableText>
                    <Button
                        size="$2"
                        circular
                        chromeless
                        onPress={(e: any) => {
                            e.stopPropagation()
                            exitTourStepFlow()
                        }}
                    >
                        <X size={16} />
                    </Button>
                </XStack>
            )}

            {/* Expanded: step detail panel — sits above the AI help button */}
            <AnimatePresence>
                {guideExpanded && (
                    <YStack
                        position="fixed"
                        bottom={84}
                        right={24}
                        width={380}
                        maxWidth="calc(100vw - 48px)"
                        backgroundColor="$background"
                        borderWidth={1}
                        borderColor="$borderColor"
                        borderRadius="$5"
                        zIndex={Z_INDEX.TOUR_FLOW}
                        elevation="$6"
                        overflow="hidden"
                        animation="quick"
                        enterStyle={{ y: 20, opacity: 0, scale: 0.97 }}
                        exitStyle={{ y: 20, opacity: 0, scale: 0.97 }}
                        height="auto"
                        maxHeight="calc(100vh - 60px)"
                    >
                        {/* Header */}
                        <XStack
                            paddingVertical="$3"
                            paddingHorizontal="$4"
                            alignItems="center"
                            borderBottomWidth={1}
                            borderBottomColor="$borderColor"
                            gap="$2"
                        >
                            <View
                                width={26}
                                height={26}
                                borderRadius={13}
                                backgroundColor="$primary"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                            >
                                <SizableText size="$1" color="white" fontWeight="700">
                                    {tourCurrentStepIndex + 1}
                                </SizableText>
                            </View>
                            <SizableText size="$4" fontWeight="600" flex={1}>
                                Step {tourCurrentStepIndex + 1} of {tourSteps.length}
                            </SizableText>
                            <Button
                                size="$2"
                                circular
                                chromeless
                                onPress={() => setGuideExpanded(false)}
                            >
                                <Minus size={18} />
                            </Button>
                        </XStack>

                        {/* Step content */}
                        <YStack padding="$4" gap="$3">
                            <SizableText size="$4">
                                {currentStep.description}
                            </SizableText>
                            {currentStep.hint && (
                                <SizableText size="$2" color="$colorMuted">
                                    {currentStep.hint}
                                </SizableText>
                            )}
                            {currentStep.tourId && !currentStep.noAutoAdvance && (
                                <SizableText size="$2" color="$colorMuted">
                                    Click the highlighted element to continue
                                </SizableText>
                            )}
                            {currentStep.noAutoAdvance && tourHighlights.length > 0 && (
                                <SizableText size="$2" color="$colorMuted">
                                    Click the highlighted area to interact, then click Next when done
                                </SizableText>
                            )}
                            {currentStep.noAutoAdvance && tourHighlights.length === 0 && (
                                <SizableText size="$2" color="$colorMuted">
                                    Enter your value, then click Next when ready
                                </SizableText>
                            )}
                        </YStack>

                        {/* Action buttons */}
                        <XStack
                            padding="$3"
                            borderTopWidth={1}
                            borderTopColor="$borderColor"
                            gap="$2"
                            justifyContent="flex-end"
                        >
                            <Button
                                size="$3"
                                variant="outlined"
                                onPress={exitTourStepFlow}
                            >
                                <X size={14} />
                                <SizableText size="$2">Exit</SizableText>
                            </Button>
                            {currentStep.noAutoAdvance && tourHighlights.length === 0 && (
                                <Button
                                    size="$3"
                                    theme="primary"
                                    onPress={skipTourStep}
                                >
                                    <ChevronRight size={14} color="white" />
                                    <SizableText size="$2" color="white">Next</SizableText>
                                </Button>
                            )}
                        </XStack>
                    </YStack>
                )}
            </AnimatePresence>
        </>,
        document.body,
    )
}

export default function OnboardingGuide() {
    const {
        expanded,
        setExpanded,
        currentStep,
        setCurrentStep,
        steps,
        completedCount,
        isLoading,
        isStepComplete,
        isInTourStepFlow,
    } = useOnboarding()

    // When tour step flow is active, render the step flow UI instead
    if (isInTourStepFlow) {
        return <TourStepFlowUI />
    }

    if (!expanded) {
        return <CollapsedButton />
    }

    const StepComponent = STEP_COMPONENTS[currentStep]
    const canGoBack = currentStep > 0
    const canGoForward = currentStep < steps.length - 1

    return (
        <YStack
            width={320}
            minWidth={320}
            height="100vh"
            borderLeftWidth={1}
            borderLeftColor="$borderColor"
            backgroundColor="$background"
            position="sticky"
            top={0}
            right={0}
            zIndex={Z_INDEX.GUIDE}
            // @ts-ignore
            style={{
                transition: 'width 300ms ease-in-out, opacity 200ms ease',
            }}
            $sm={{
                position: 'fixed',
                right: 0,
                top: 0,
                width: 320,
                elevation: '$6',
            }}
        >
            {/* Header */}
            <XStack
                paddingHorizontal="$4"
                paddingVertical="$3"
                alignItems="center"
                justifyContent="space-between"
                borderBottomWidth={1}
                borderBottomColor="$borderColor"
            >
                <SizableText size="$5" fontWeight="700">
                    Getting Started
                </SizableText>
                <XStack gap="$1" alignItems="center">
                    <SizableText size="$2" color="$colorMuted">
                        {completedCount}/{steps.length}
                    </SizableText>
                    <Button
                        size="$2"
                        circular
                        chromeless
                        onPress={() => setExpanded(false)}
                    >
                        <X size={16} />
                    </Button>
                </XStack>
            </XStack>

            {/* Progress dots */}
            <ProgressDots />

            {/* Step content */}
            <YStack flex={1} overflow="auto" paddingHorizontal="$4" paddingBottom="$4">
                {isLoading ? (
                    <View flex={1} alignItems="center" justifyContent="center">
                        <Spinner />
                    </View>
                ) : (
                    <StepComponent />
                )}
            </YStack>

            {/* Footer navigation */}
            <XStack
                paddingHorizontal="$4"
                paddingVertical="$3"
                borderTopWidth={1}
                borderTopColor="$borderColor"
                justifyContent="space-between"
                alignItems="center"
            >
                <Button
                    size="$3"
                    chromeless
                    disabled={!canGoBack}
                    opacity={canGoBack ? 1 : 0.3}
                    onPress={() => setCurrentStep(currentStep - 1)}
                    icon={<ChevronLeft size={16} />}
                >
                    Back
                </Button>
                {canGoForward ? (
                    <Button
                        size="$3"
                        chromeless
                        onPress={() => setCurrentStep(currentStep + 1)}
                        iconAfter={<ChevronRight size={16} />}
                    >
                        Skip
                    </Button>
                ) : (
                    <View />
                )}
            </XStack>
        </YStack>
    )
}
